import React, { useCallback, useEffect, useState } from "react";
import { Eye, RefreshCw, Sparkles, Zap } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const relativeTime = (value) => {
  if (!value) return "Ahora";
  const ms = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "Ahora";
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 1) return "Ahora";
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours} h`;
  return `Hace ${Math.floor(hours / 24)} d`;
};

const WallCard = ({ item }) => (
  <article
    className="kd9-card p-4 sm:p-5 space-y-4"
    data-testid={item.synthetic ? "oracle-pulse-card" : "oracle-real-card"}
  >
    <div className="flex flex-wrap items-center gap-2">
      <span
        className="font-mont text-[9px] uppercase tracking-[.18em] px-2 py-1 rounded-full border"
        style={{
          borderColor: item.synthetic ? "rgba(212,175,55,.35)" : "var(--kd9-border)",
          color: item.synthetic ? "var(--kd9-gold)" : "var(--kd9-text-soft)",
        }}
      >
        {item.synthetic ? "PULSO KD9 · SISTEMA" : item.deck_name || "ORÁCULO KD9"}
      </span>
      <span className="font-mont text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
        {item.public_alias || "Usuario KD9"}
      </span>
      <span className="ml-auto font-mont text-[9px]" style={{ color: "var(--kd9-text-soft)" }}>
        {relativeTime(item.created_at)}
      </span>
    </div>

    <p className="font-cormorant text-xl sm:text-2xl leading-relaxed">{item.question}</p>

    {!!item.cards?.length && (
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {item.cards.slice(0, 3).map((card) => (
          <div
            key={card.position || card.card_key}
            className="rounded-xl overflow-hidden border min-h-[118px] sm:min-h-[145px] flex flex-col"
            style={{ borderColor: "var(--kd9-border)", background: "rgba(0,0,0,.2)" }}
          >
            {card.image_url ? (
              <img src={card.image_url} alt={card.name || "Carta"} className="w-full aspect-[2/3] object-cover" loading="lazy" />
            ) : (
              <div className="flex-1 grid place-items-center text-2xl kd9-gold-text">
                {card.art_symbol || "✦"}
              </div>
            )}
            <div className="px-2 py-2 border-t" style={{ borderColor: "var(--kd9-border)" }}>
              <div className="font-cinzel text-[9px] sm:text-[10px] kd9-gold-text leading-tight">{card.name}</div>
              <div className="font-mont text-[7px] mt-1 opacity-60">{card.position_label}</div>
            </div>
          </div>
        ))}
      </div>
    )}

    {!!item.responses?.length && (
      <div className="space-y-3 pt-1">
        {item.responses.slice(0, 2).map((response) => (
          <div
            key={response.id}
            className="rounded-xl border p-3 sm:p-4"
            style={{ borderColor: "var(--kd9-border)", background: "rgba(0,0,0,.14)" }}
          >
            <div className="font-cinzel kd9-gold-text text-xs mb-2">{response.consultant_name}</div>
            {response.answer_type==="audio" ? <div className="space-y-2">
              <div className="font-mont text-[9px] uppercase tracking-[.14em]" style={{color:"var(--kd9-text-soft)"}}>🎙️ Respuesta de voz del consultor</div>
              {response.transcript
                ? <p className="font-cormorant text-base sm:text-lg leading-relaxed whitespace-pre-wrap">{response.transcript}</p>
                : <p className="font-cormorant text-base sm:text-lg leading-relaxed" style={{color:"var(--kd9-text-soft)"}}>Inicia sesión para escuchar esta respuesta de voz.</p>}
            </div> : <p className="font-cormorant text-base sm:text-lg leading-relaxed whitespace-pre-wrap">{response.answer}</p>}
          </div>
        ))}
      </div>
    )}
  </article>
);

const PublicOracleWall = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [data, setData] = useState({ items: [], enabled: true, question_cost: 0, unit: "KD" });
  const [loading, setLoading] = useState(true);
  const [gateMessage, setGateMessage] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: response } = await api.get("/api/oracle/public/wall", { params: { limit: embedded ? 8 : 12 } });
      setData({
        items: Array.isArray(response?.items) ? response.items : [],
        enabled: response?.enabled !== false,
        question_cost: Number(response?.question_cost || 0),
        unit: response?.unit || "KD",
        pulse_disclosure: response?.pulse_disclosure || "",
      });
    } catch (_) {
      setData((current) => ({ ...current, items: [] }));
    } finally {
      setLoading(false);
    }
  }, [embedded]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  const ask = async () => {
    setGateMessage("");
    if (!ready) return;
    if (!user) {
      navigate("/register?next=%2Fapp%2Foraculo");
      return;
    }
    try {
      const { data: preflight } = await api.get("/api/oracle/preflight");
      if (preflight?.can_ask) {
        navigate("/app/oraculo");
        return;
      }
      if (preflight?.reason === "INSUFFICIENT_ENERGY" || preflight?.reason === "WALLET_FROZEN") {
        setGateMessage(
          preflight?.reason === "WALLET_FROZEN"
            ? "Tu billetera no está disponible para nuevas consultas."
            : `Necesitas al menos ⚡ ${preflight.question_cost} ${preflight.unit || "KD"} para publicar la pregunta.`
        );
        navigate("/app/energia-kd?from=oraculo");
        return;
      }
      setGateMessage("El Oráculo está temporalmente desactivado para nuevas preguntas.");
    } catch (error) {
      setGateMessage(formatApiError(error));
    }
  };

  return (
    <section
      id="muro-oraculo"
      className={embedded ? "relative pt-5" : "relative max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-10"}
      data-testid="public-oracle-wall"
    >
      <div className="kd9-card p-5 md:p-8 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-5">
          <div className="flex-1">
            <div className="font-mont text-[9px] tracking-[.35em] uppercase mb-2" style={{ color: "var(--kd9-accent)" }}>
              · EL ORÁCULO RESPIRA ·
            </div>
            <h2 className="font-cinzel kd9-gold-text text-2xl md:text-3xl tracking-[.1em]">MURO DEL ORÁCULO</h2>
            <p className="font-cormorant text-lg mt-2 max-w-3xl" style={{ color: "var(--kd9-text-soft)" }}>
              Puedes observar el muro sin cuenta. Para formular una pregunta debes iniciar sesión y disponer de Energía KD9 suficiente para el costo vigente.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="kd9-btn-outline min-h-11 px-4 py-2 rounded-lg text-[10px] inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> ACTUALIZAR
          </button>
        </div>

        <div className="rounded-xl border p-4 mb-5 flex flex-col md:flex-row md:items-center gap-3" style={{ borderColor: "var(--kd9-border)", background: "rgba(0,0,0,.15)" }}>
          <div className="flex items-center gap-2 font-mont text-[10px]">
            <Eye size={14} className="kd9-gold-text" />
            LECTURA PÚBLICA DEL MURO
          </div>
          <div className="md:ml-auto font-cinzel text-sm">
            Pregunta: <span className="kd9-gold-text">{data.question_cost > 0 ? `⚡ ${data.question_cost} ${data.unit}` : "GRATIS"}</span>
          </div>
          <button
            type="button"
            onClick={ask}
            disabled={!ready || !data.enabled}
            className="kd9-btn-primary min-h-11 px-5 py-3 rounded-lg text-[10px] disabled:opacity-50"
          >
            <Sparkles size={13} className="inline mr-2" />
            {user ? "HACER MI PREGUNTA" : "CREAR CUENTA PARA PREGUNTAR"}
          </button>
        </div>

        {gateMessage && (
          <div className="rounded-xl border p-3 mb-4 font-mont text-xs" style={{ borderColor: "rgba(245,158,11,.35)", color: "var(--kd9-text-soft)" }}>
            {gateMessage}
          </div>
        )}

        {!user && ready && (
          <div className="font-mont text-[9px] mb-4" style={{ color: "var(--kd9-text-soft)" }}>
            ¿Ya tienes cuenta? <Link to="/login?next=%2Fapp%2Foraculo" className="kd9-gold-text underline">Inicia sesión</Link>.
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-4">
          {data.items.map((item) => <WallCard key={item.id} item={item} />)}
        </div>

        {!loading && !data.items.length && (
          <div className="rounded-xl border p-8 text-center font-cormorant text-lg" style={{ borderColor: "var(--kd9-border)" }}>
            El Muro del Oráculo está recuperando su pulso. Actualiza en unos instantes.
          </div>
        )}

        {data.pulse_disclosure && (
          <div className="mt-5 flex items-start gap-2 font-mont text-[8px] leading-relaxed opacity-55">
            <Zap size={12} className="shrink-0 mt-0.5" />
            {data.pulse_disclosure}
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicOracleWall;
